// frontend/src/services/boardingService.js
import axios from '../axiosConfig';

/**
 * boardingService
 *
 * Frontend service for QR Code Boarding Pass System
 * Handles all API calls related to boarding pass generation, scanning, and tracking
 */

const boardingService = {
  /**
   * CHECK: Check if boarding tracking is available
   * Validates both global is_active and entity-specific setting
   *
   * @param {number} entityId - Entity ID
   * @returns {Promise<object>} Availability status {available, globallyActive, entityEnabled}
   */
  async checkBoardingAvailability(entityId) {
    try {
      const response = await axios.get(`/api/boarding/${entityId}/availability`);
      return response.data;
    } catch (error) {
      console.error('Error checking boarding availability:', error);
      // Return false on error to hide boarding features
      return {
        success: false,
        data: {
          available: false,
          globallyActive: false,
          entityEnabled: false
        }
      };
    }
  },

  /**
   * RIDER: Get or generate boarding pass for a booking
   *
   * @param {number} entityId - Entity ID
   * @param {number} bookingId - Booking ID
   * @param {number|null} tripId - Trip ID for recurring booking occurrence scope
   * @returns {Promise<object>} Boarding pass data with QR code
   */
  async getBoardingPass(entityId, bookingId, tripId = null) {
    try {
      const response = await axios.get(
        `/api/boarding/${entityId}/boarding-pass/${bookingId}`,
        {
          params: tripId ? { tripId } : {}
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting boarding pass:', error);
      throw error;
    }
  },

  /**
   * DRIVER: Scan boarding pass QR code and mark passenger as boarded
   *
   * @param {number} entityId - Entity ID
   * @param {number} tripId - Trip ID
   * @param {string} boardingPassCode - Scanned QR code value
   * @param {number} ridersBoarded - Actual number of riders (optional, defaults to booking.riders)
   * @param {number} luggageLoaded - Actual number of luggage pieces loaded (optional, defaults to booking.luggages)
   * @param {object} location - GPS location {lat, lng} (optional)
   * @returns {Promise<object>} Boarding confirmation
   */
  async scanBoardingPass(entityId, tripId, boardingPassCode, ridersBoarded = null, luggageLoaded = null, location = null) {
    try {
      const response = await axios.post(
        `/api/boarding/${entityId}/trips/${tripId}/scan-boarding-pass`,
        {
          boardingPassCode,
          ridersBoarded,
          luggageLoaded,
          location
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error scanning boarding pass:', error);
      throw error;
    }
  },

  /**
   * DRIVER: Manually board passenger (fallback method)
   *
   * @param {number} entityId - Entity ID
   * @param {number} tripId - Trip ID
   * @param {number} bookingId - Booking ID
   * @param {number} ridersBoarded - Actual number of riders (optional)
   * @param {number} luggageLoaded - Actual number of luggage pieces loaded (optional)
   * @param {object} location - GPS location {lat, lng} (optional)
   * @param {string} notes - Optional notes
   * @returns {Promise<object>} Boarding confirmation
   */
  async manualBoardPassenger(entityId, tripId, bookingId, ridersBoarded = null, luggageLoaded = null, location = null, notes = null) {
    try {
      const response = await axios.post(
        `/api/boarding/${entityId}/trips/${tripId}/manual-board/${bookingId}`,
        {
          ridersBoarded,
          luggageLoaded,
          location,
          notes
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error with manual boarding:', error);
      throw error;
    }
  },

  /**
   * DRIVER: Update boarding counts for already boarded passenger
   *
   * @param {number} entityId - Entity ID
   * @param {number} tripId - Trip ID
   * @param {number} bookingId - Booking ID
   * @param {number} ridersBoarded - Corrected number of riders
   * @param {number} luggageLoaded - Corrected number of luggage pieces
   * @param {string} notes - Optional notes explaining the correction
   * @returns {Promise<object>} Update confirmation
   */
  async updateBoardingCounts(entityId, tripId, bookingId, ridersBoarded, luggageLoaded, notes = null) {
    try {
      const response = await axios.put(
        `/api/boarding/${entityId}/trips/${tripId}/update-counts/${bookingId}`,
        {
          ridersBoarded,
          luggageLoaded,
          notes
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating boarding counts:', error);
      throw error;
    }
  },

  /**
   * DRIVER: Mark passenger as no-show
   *
   * @param {number} entityId - Entity ID
   * @param {number} tripId - Trip ID
   * @param {number} bookingId - Booking ID
   * @param {string} notes - Reason for no-show (optional)
   * @returns {Promise<object>} No-show confirmation
   */
  async markAsNoShow(entityId, tripId, bookingId, notes = null) {
    try {
      const response = await axios.post(
        `/api/boarding/${entityId}/trips/${tripId}/mark-no-show/${bookingId}`,
        { notes }
      );
      return response.data;
    } catch (error) {
      console.error('Error marking no-show:', error);
      throw error;
    }
  },

  /**
   * DRIVER: Get boarding status for a trip
   *
   * @param {number} entityId - Entity ID
   * @param {number} tripId - Trip ID
   * @returns {Promise<object>} Boarding status summary with all bookings
   */
  async getTripBoardingStatus(entityId, tripId) {
    try {
      const response = await axios.get(
        `/api/boarding/${entityId}/trips/${tripId}/boarding-status`
      );
      return response.data;
    } catch (error) {
      console.error('Error getting boarding status:', error);
      throw error;
    }
  },

  /**
   * ADMIN: Get boarding analytics
   *
   * @param {number} entityId - Entity ID
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @returns {Promise<object>} Boarding analytics data
   */
  async getBoardingAnalytics(entityId, startDate, endDate) {
    try {
      const response = await axios.get(
        `/api/boarding/${entityId}/analytics`,
        {
          params: { startDate, endDate }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting boarding analytics:', error);
      throw error;
    }
  },

  /**
   * Generate QR code data URL for display
   * Uses qrcode library to generate QR code image
   *
   * @param {object} qrData - QR code payload data
   * @returns {Promise<string>} Data URL for QR code image
   */
  async generateQRCodeDataURL(qrData) {
    try {
      // Dynamic import of qrcode library
      const QRCode = (await import('qrcode')).default;

      const qrString = JSON.stringify(qrData);
      const dataURL = await QRCode.toDataURL(qrString, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      return dataURL;
    } catch (error) {
      console.error('Error generating QR code:', error);
      throw error;
    }
  },

  /**
   * Download boarding pass as image
   *
   * @param {string} qrCodeDataURL - QR code data URL
   * @param {string} bookingId - Booking ID for filename
   */
  downloadBoardingPass(qrCodeDataURL, bookingId) {
    const link = document.createElement('a');
    link.href = qrCodeDataURL;
    link.download = `boarding-pass-${bookingId}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  /**
   * Print boarding pass
   * Opens print dialog with QR code
   *
   * @param {string} qrCodeDataURL - QR code data URL
   * @param {object} bookingDetails - Booking details to display
   */
  printBoardingPass(qrCodeDataURL, bookingDetails) {
    const printWindow = window.open('', '_blank');

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Boarding Pass - ${bookingDetails.bookingId}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
              margin: 0;
              padding: 20px;
            }
            .boarding-pass {
              text-align: center;
              max-width: 400px;
            }
            h1 {
              font-size: 24px;
              margin-bottom: 10px;
            }
            .qr-code {
              margin: 20px 0;
            }
            .qr-code img {
              max-width: 300px;
              height: auto;
            }
            .details {
              text-align: left;
              margin-top: 20px;
              padding: 15px;
              border: 1px solid #ddd;
              border-radius: 8px;
            }
            .detail-row {
              margin: 8px 0;
              display: flex;
              justify-content: space-between;
            }
            .label {
              font-weight: bold;
              color: #666;
            }
            @media print {
              body {
                display: block;
              }
            }
          </style>
        </head>
        <body>
          <div class="boarding-pass">
            <h1>Boarding Pass</h1>
            <div class="qr-code">
              <img src="${qrCodeDataURL}" alt="Boarding Pass QR Code" />
            </div>
            <div class="details">
              <div class="detail-row">
                <span class="label">Booking ID:</span>
                <span>${bookingDetails.bookingId}</span>
              </div>
              ${bookingDetails.riderName ? `
              <div class="detail-row">
                <span class="label">Name:</span>
                <span>${bookingDetails.riderName}</span>
              </div>
              ` : ''}
              <div class="detail-row">
                <span class="label">Riders:</span>
                <span>${bookingDetails.riders}</span>
              </div>
              <div class="detail-row">
                <span class="label">Luggage:</span>
                <span>${bookingDetails.luggages ?? 0}</span>
              </div>
              <div class="detail-row">
                <span class="label">Date:</span>
                <span>${bookingDetails.tripDate}</span>
              </div>
              <div class="detail-row">
                <span class="label">Pickup:</span>
                <span>${bookingDetails.pickup}</span>
              </div>
              <div class="detail-row">
                <span class="label">Dropoff:</span>
                <span>${bookingDetails.dropoff}</span>
              </div>
            </div>
            <p style="margin-top: 20px; color: #666; font-size: 12px;">
              Please show this QR code to the driver when boarding
            </p>
          </div>
          <script>
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  },

  /**
   * Get current GPS location (for drivers)
   *
   * @returns {Promise<object>} Location object {lat, lng} or null
   */
  async getCurrentLocation() {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        console.warn('Geolocation not supported');
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.warn('Error getting location:', error);
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      );
    });
  }
};

export default boardingService;
